import React, { useState, useEffect, useMemo } from "react";
import {
  Text,
  StyleSheet,
  View,
  Dimensions,
  Platform,
  TouchableOpacity,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../store";
import { resetBothInspectionAndSelectedWalk } from "../../store/slices/sparcWalkInspectionSlice";
import LHConfirmationDialog from "../../components/LHConfirmationDialog";
import DLOBreadcrumb, {
  DLOBreadcrumbItem,
} from "../../modules/dlo/components/DLOBreadcrumb";
import LHSectionHeader from "../../components/LHSectionHeader";
import { FontFamily } from "../../constants/fonts";
import Svg, { Path } from "react-native-svg";
import { useTheme } from "../../theme";

const { width: screenWidth } = Dimensions.get("window");

// iOS Mobile Back Arrow Icon (from Figma)
const BackArrowIcon = ({ color = "#005CBC" }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M16.6666 9.16663H6.52497L11.1833 4.50829L9.99997 3.33329L3.3333 9.99996L9.99997 16.6666L11.175 15.4916L6.52497 10.8333H16.6666V9.16663Z"
      fill={color}
    />
  </Svg>
);

interface SparcWalkInspectionHeaderProps {
  title: string;
  subtitle: string;
  onReturn?: () => void;
  onNavigateHome?: () => void;
  isReadOnly?: boolean;
}

const SparcWalkInspectionHeader: React.FC<SparcWalkInspectionHeaderProps> = ({
  title,
  subtitle,
  onReturn,
  onNavigateHome,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const dispatch = useDispatch<AppDispatch>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState<
    "home" | "sparcwalk"
  >("sparcwalk");
  const [initialData, setInitialData] = useState<any>(null);
  const [hasSessionChanges, setHasSessionChanges] = useState(false);
  const [isDataStabilized, setIsDataStabilized] = useState(false);

  // Get status from Redux
  const status = useSelector(
    (state: any) => state.sparcWalkInspection.walkState
  );

  // Get inspection data from Redux to check for changes
  // Use individual selectors to avoid creating new object references on every render
  const selectedBehaviorIds = useSelector(
    (state: any) => state.sparcWalkInspection.selectedBehaviorIds
  );
  const selectedQuestionIds = useSelector(
    (state: any) => state.sparcWalkInspection.selectedQuestionIds
  );
  const expectedAnswers = useSelector(
    (state: any) => state.sparcWalkInspection.expectedAnswers
  );
  const observations = useSelector(
    (state: any) => state.sparcWalkInspection.observations
  );
  const possibleSolutions = useSelector(
    (state: any) => state.sparcWalkInspection.possibleSolutions
  );
  const selectedEmployeeId = useSelector(
    (state: any) => state.sparcWalkInspection.selectedEmployeeId
  );
  const managerSignatureFileName = useSelector(
    (state: any) => state.sparcWalkInspection.managerSignatureFileName
  );
  const managerSignatureImageUrl = useSelector(
    (state: any) => state.sparcWalkInspection.managerSignatureImageUrl
  );
  const miscNotes = useSelector(
    (state: any) => state.sparcWalkInspection.miscNotes
  );
  const miscObservationImages = useSelector(
    (state: any) => state.sparcWalkInspection.miscObservationImages
  );
  const miscellaneousObservations = useSelector(
    (state: any) => state.sparcWalkInspection.miscellaneousObservations
  );
  const images = useSelector(
    (state: any) => state.sparcWalkInspection.images
  );

  // Memoize inspectionData to prevent new object creation on every render
  const inspectionData = useMemo(
    () => ({
      selectedBehaviorIds,
      selectedQuestionIds,
      expectedAnswers,
      observations,
      possibleSolutions,
      selectedEmployeeId,
      managerSignatureFileName,
      managerSignatureImageUrl,
      miscNotes,
      miscObservationImages,
      miscellaneousObservations,
      images,
    }),
    [
      selectedBehaviorIds,
      selectedQuestionIds,
      expectedAnswers,
      observations,
      possibleSolutions,
      selectedEmployeeId,
      managerSignatureFileName,
      managerSignatureImageUrl,
      miscNotes,
      miscObservationImages,
      miscellaneousObservations,
      images,
    ]
  );

  // Get the selected walk to detect when a new walk is loaded
  const selectedWalk = useSelector(
    (state: any) => state.sparcWalk.selectedWalk
  );

  // Reset change tracking when a new walk is loaded
  useEffect(() => {
    // Reset initial data and change tracking when selectedWalk changes (new walk loaded)
    setInitialData(null);
    setIsDataStabilized(false);
    setHasSessionChanges(false);
  }, [selectedWalk]);

  // Capture initial data after a delay to ensure all data is loaded and stable
  useEffect(() => {
    if (!initialData) {
      // Delay initial data capture to ensure all data is loaded and stable
      const timer = setTimeout(() => {
        setInitialData(JSON.parse(JSON.stringify(inspectionData)));
        setIsDataStabilized(true);
      }, 1000); // 1 second delay to allow for any remaining async operations

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Track changes by comparing current data with initial data, but only after data is stabilized
  useEffect(() => {
    if (initialData && isDataStabilized) {
      // Only track changes if we have meaningful data (not just empty objects)
      const hasBehaviors = inspectionData.selectedBehaviorIds && inspectionData.selectedBehaviorIds.length > 0;
      const hasQuestions = inspectionData.selectedQuestionIds && inspectionData.selectedQuestionIds.length > 0;
      const hasAnswers = inspectionData.expectedAnswers && inspectionData.expectedAnswers.trim() !== '';
      const hasObservations = inspectionData.observations && inspectionData.observations.trim() !== '';
      const hasSolutions = inspectionData.possibleSolutions && inspectionData.possibleSolutions.trim() !== '';
      const hasEmployee = inspectionData.selectedEmployeeId && inspectionData.selectedEmployeeId.trim() !== '';
      const hasSignature = inspectionData.managerSignatureImageUrl && inspectionData.managerSignatureImageUrl.trim() !== '';
      const hasMiscNotes = inspectionData.miscNotes && inspectionData.miscNotes.trim() !== '';
      const hasImages = inspectionData.images && inspectionData.images.length > 0;
      const hasMiscImages = inspectionData.miscObservationImages && inspectionData.miscObservationImages.length > 0;
      
      // Only consider it a change if there's actual user-inputted data
      const hasActualData = hasBehaviors || hasQuestions || hasAnswers || hasObservations || 
                            hasSolutions || hasEmployee || hasSignature || hasMiscNotes || 
                            hasImages || hasMiscImages;
      
      if (hasActualData) {
        const hasChanges = JSON.stringify(inspectionData) !== JSON.stringify(initialData);
        setHasSessionChanges(hasChanges);
      } else {
        // If no actual data, don't consider it as changes
        setHasSessionChanges(false);
      }
    }
  }, [inspectionData, initialData, isDataStabilized]);

  // Function to check if there are unsaved changes made during this session
  const hasUnsavedChanges = (): boolean => {
    // Only consider it as changes if there's actual user-inputted data
    const hasBehaviors = inspectionData.selectedBehaviorIds && inspectionData.selectedBehaviorIds.length > 0;
    const hasQuestions = inspectionData.selectedQuestionIds && inspectionData.selectedQuestionIds.length > 0;
    const hasAnswers = inspectionData.expectedAnswers && inspectionData.expectedAnswers.trim() !== '';
    const hasObservations = inspectionData.observations && inspectionData.observations.trim() !== '';
    const hasSolutions = inspectionData.possibleSolutions && inspectionData.possibleSolutions.trim() !== '';
    const hasEmployee = inspectionData.selectedEmployeeId && inspectionData.selectedEmployeeId.trim() !== '';
    const hasSignature = inspectionData.managerSignatureImageUrl && inspectionData.managerSignatureImageUrl.trim() !== '';
    const hasMiscNotes = inspectionData.miscNotes && inspectionData.miscNotes.trim() !== '';
    const hasImages = inspectionData.images && inspectionData.images.length > 0;
    const hasMiscImages = inspectionData.miscObservationImages && inspectionData.miscObservationImages.length > 0;
    
    // If there's no meaningful data, don't show confirmation
    const hasActualData = hasBehaviors || hasQuestions || hasAnswers || hasObservations || 
                          hasSolutions || hasEmployee || hasSignature || hasMiscNotes || 
                          hasImages || hasMiscImages;
    
    if (!hasActualData) {
      return false;
    }
    
    // Only return true if data is stabilized and changes have been made during this session
    return isDataStabilized && hasSessionChanges;
  };

  // Handle Back click and show confirmation dialog only if there are unsaved changes
  const handleReturnClick = () => {
    if (status === 'completed') {
      dispatch(resetBothInspectionAndSelectedWalk());
      console.log('SparcWalkInspection state and selectedWalk have been reset');
      if (onReturn) {
        onReturn();
      }
      return;
    }
    // Show confirmation only if there are unsaved changes and walk is not completed
    if ((status === 'in-progress' || status === 'new') && hasUnsavedChanges()) {
      setNavigationTarget('sparcwalk');
      setShowConfirmDialog(true);
    } else {
      // If no changes or completed status, just go back immediately
      dispatch(resetBothInspectionAndSelectedWalk());
      console.log('SparcWalkInspection state and selectedWalk have been reset');
      if (onReturn) {
        onReturn();
      }
    }
  };

  // Handle confirmation to navigate back or home
  const handleConfirm = () => {
    // Reset both the SPARC Walk inspection state and selectedWalk
    dispatch(resetBothInspectionAndSelectedWalk());
    console.log('SparcWalkInspection state and selectedWalk have been reset');
    setShowConfirmDialog(false);
    
    // Reset session changes flag
    setHasSessionChanges(false);

    // Navigate based on the target
    if (navigationTarget === 'home' && onNavigateHome) {
      onNavigateHome();
    } else if (navigationTarget === 'sparcwalk' && onReturn) {
      onReturn();
    }
  };

  // Handle cancellation of navigation
  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  // Handle Home navigation
  const handleHomeClick = () => {
    if (status === 'completed') {
      dispatch(resetBothInspectionAndSelectedWalk());
      if (onNavigateHome) {
        onNavigateHome();
      }
      return;
    }
    // Show confirmation only if there are unsaved changes and walk is not completed
    if ((status === 'in-progress' || status === 'new') && hasUnsavedChanges()) {
      setNavigationTarget('home');
      setShowConfirmDialog(true);
    } else {
      // If no changes or completed status, just navigate immediately
      dispatch(resetBothInspectionAndSelectedWalk());
      if (onNavigateHome) {
        onNavigateHome();
      }
    }
  };

  // Create breadcrumb items
  const breadcrumbItems: DLOBreadcrumbItem[] = [
    {
      id: "sparcwalk",
      label: "SPARC Walk",
      onPress: handleReturnClick,
    },
    {
      id: "inspection",
      label: "Inspection",
      isCurrentPage: true,
    },
  ];

  // Check if iOS mobile
  const isIOSMobile = Platform.OS === "ios" && screenWidth < 768;

  return (
    <>
      <View style={styles.header}>
        {/* Breadcrumb Navigation - iOS Mobile vs Desktop/Web */}
        <View style={styles.breadcrumbContainer}>
          {isIOSMobile ? (
            // iOS Mobile: Simple back button matching Figma design
            <TouchableOpacity
              style={styles.mobileBackButton}
              onPress={handleReturnClick}
              activeOpacity={0.7}
            >
              <BackArrowIcon color={isDark ? colors.brand.primary : "#005CBC"} />
              <Text
                style={[
                  styles.mobileBackButtonText,
                  isDark && { color: colors.brand.primary },
                ]}
              >
                Back to SparcWalk
              </Text>
            </TouchableOpacity>
          ) : (
            // Desktop/Web: Full breadcrumb
            <DLOBreadcrumb
              items={breadcrumbItems}
              showHomeIcon={true}
              onHomePress={handleHomeClick}
            />
          )}
        </View>

        {/* Title and subtitle */}
        <View style={styles.leftGroup}>
          <View style={styles.textGroup}>
            <Text style={[styles.title, { color: colors.text.primary }]}>{title}</Text>
            <Text style={[styles.subtitle, { color: colors.text.secondary }]}>{subtitle}</Text>
          </View>
        </View>
      </View>

      {/* Card with integrated header and body (matches other SPARC components) */}
      <View
        style={[
          styles.cardContainer,
          isDark && { backgroundColor: colors.background.surface },
        ]}
      >
        <LHSectionHeader
          title="Complete before going to the floor"
          containerStyle={
            isDark
              ? { ...styles.sectionHeader, backgroundColor: colors.brand.primary }
              : styles.sectionHeader
          }
          textStyle={styles.header1text}
        />

        <View
          style={[
            styles.instructionContainer,
            isDark && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
          ]}
        >
          <View style={styles.instructionRow}>
            <Text style={[styles.bulletPoint, { color: colors.text.primary }]}>•</Text>
            <Text style={[styles.instructionLine, { color: colors.text.primary }]}> 
              Inform others that you are coming and explain the reason for your
              visit
            </Text>
          </View>
          <View style={styles.instructionRow}>
            <Text style={[styles.bulletPoint, { color: colors.text.primary }]}>•</Text>
            <Text style={[styles.instructionLine, { color: colors.text.primary }]}> 
              Observe something specific, for example: Visit performance board
              and huddle. Walk the line, observe a process or system, and
              interact
            </Text>
          </View>
          <View style={styles.instructionRow}>
            <Text style={[styles.bulletPoint, { color: colors.text.primary }]}>•</Text>
            <Text style={[styles.instructionLine, { color: colors.text.primary }]}> 
              Celebrate successes and acknowledge achievements
            </Text>
          </View>
          <View style={[styles.instructionRow, styles.lastInstructionRow]}>
            <Text style={[styles.bulletPoint, { color: colors.text.primary }]}>•</Text>
            <Text style={[styles.instructionLine, { color: colors.text.primary }]}> 
              Identify what actions are needed and follow through on them
            </Text>
          </View>
        </View>
      </View>

      <LHConfirmationDialog
        visible={showConfirmDialog}
        message="You have unsaved data, are you sure you want to go back?"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

const styles = StyleSheet.create({
  header: {
    width: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    paddingTop: screenWidth < 600 ? 16 : 16,
  },
  leftGroup: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    marginTop: 8,
  },
  breadcrumbContainer: {
    marginBottom: 12,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    overflow: "hidden",
    marginLeft: -6,
  },
  textGroup: {
    flexShrink: 1,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    fontStyle: "normal",
    fontSize: screenWidth < 600 ? 20 : 28,
    lineHeight: screenWidth < 600 ? 26 : 36,
    letterSpacing: 0,
    color: "#FFFFFF",
  },
  subtitle: {
    fontFamily: FontFamily.regular,
    fontWeight: "400",
    fontStyle: "normal",
    fontSize: screenWidth < 600 ? 12 : 16,
    lineHeight: screenWidth < 600 ? 16 : 20,
    letterSpacing: 0,
    color: "#FFFFFF",
    marginTop: 2,
  },
  // iOS Mobile back button styles (matching Figma)
  mobileBackButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    height: 24,
    paddingVertical: 2,
    paddingHorizontal: 4,
  },
  mobileBackButtonText: {
    color: "#005CBC",
    fontFamily: FontFamily.regular,
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 20,
  },
  returnButton: {
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    backgroundColor: "rgba(0,102,204,0.1)",
    borderRadius: 6,
  },
  returnText: {
    marginLeft: 6,
    color: "#0066CC",
    fontSize: 14,
    fontWeight: "500",
  },
  cardContainer: {
    marginTop: 16,
    borderRadius: 6,
    // allow header to expand beyond original bounds when wrapping
    overflow: Platform.OS === "ios" ? "visible" : "hidden",
  },
  sectionHeader: {
    backgroundColor: "#3582E6",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    // use minHeight so header can expand to 2 lines when needed
    minHeight: 48,
  },
  header1text: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
  instructionContainer: {
    paddingTop: 12,
    paddingBottom: 10,
    borderColor: "#e9ecef",
    borderWidth: 1,
    borderTopWidth: 0,
    borderRadius: 6,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    backgroundColor: "white",
    padding: 16,
  },
  instructionRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 6,
  },
  lastInstructionRow: {
    marginBottom: 0,
  },
  bulletPoint: {
    fontWeight: "700",
    marginRight: 8,
    color: "#FFFFFF",
  },
  instructionLine: {
    flex: 1,
    fontFamily: FontFamily.regular,
    color: "#FFFFFF",
    fontSize: 14,
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: 20,
  },
});

export default SparcWalkInspectionHeader;
